import { Input, Component, HostListener } from '@angular/core';

import { catchError, map, mergeMap, of, take } from 'rxjs';
import { MessageService } from 'primeng/api';

import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { BriefingsService } from '@Monitor/Briefings/services/briefings.service';
import { BriefingModel } from '@Monitor/Briefings/models/briefing.model';
import { BriefingHistoryModel } from '@Monitor/Briefings/models/briefing-history.model';
import { LocalizationService } from '@Core/Localization/services/localization.service';

@Component({
    selector: 'agpr-briefing-generating-pdf-dialog',
    templateUrl: './briefing-generating-pdf-dialog.component.html',
    styleUrls: ['./briefing-generating-pdf-dialog.component.scss'],
    standalone: false
})
export class BriefingGeneratingPdfDialogComponent extends AbstractDialogComponent {
    @Input() public iframeElement: HTMLIFrameElement = null;
    @Input() public isClean: boolean = true;
    @Input() public briefing: BriefingModel;
    @Input() public briefingHistory: BriefingHistoryModel;
    @Input() public isReportBriefing: boolean = false;
    @Input() public includeMentionsInPDF: boolean = false;

    public readonly messageSeverity = messageSeverityEnum;
    public readonly buttonTypes = ButtonTypesEnum;

    pagesGenerated: number = 0;
    totalPages: number = 0;
    started: boolean = false;
    clickTimeout: NodeJS.Timeout = null;
    confirmGenerate: boolean = false;
    progress: {
        currentIndex: number;
        totalItems: number;
    } = {
        currentIndex: 0,
        totalItems: 0
    };
    reservedUrl: string;

    constructor(
        public coreDialogService: CoreDialogService,
        public briefingsService: BriefingsService,
        private messageService: MessageService,
        private localizationService: LocalizationService,
    ) {
        super(coreDialogService, 'BriefingGeneratingPDF');
    }

    @HostListener('window:message', ['$event'])
    onMessage(event: MessageEvent) {
        if (event?.data === 'canvasPageDoneLoading') {
            // this is triggered from the canvas iframe, which means that the canvas has loaded
            // I added an extra 1 second to let the images, charts & miniprofiles render (just as a buffer)
            // we can maybe further improve this, but for now it should be ok
            this.progress = {
                currentIndex: 0,
                totalItems: 0
            };
            this.startGeneratingPDF();
        } if (event?.data === 'generatingPdfCompleted') {
            this.started = false;
            this.messageService.add({
                severity: messageSeverityEnum.success,
                summary: 'Briefing.pdf.generate.success',
                detail: this.localizationService.transform('Briefing.pdf.generate.success.message'),
            });
            this.closeDialog();
        } else if (event?.data?.generatingPDF) {
            this.started = true;
            this.pagesGenerated = event?.data?.generatingPDF?.pagesGenerated;
            this.progress = event?.data?.generatingPDF?.progress;
        } else if (event?.data === 'generatingPdfFailed') {
            this.messageService.add(
                {
                    severity: messageSeverityEnum.error,
                    summary: 'Briefing.pdf.generate.failed',
                    detail: 'Briefing.pdf.generate.error'
                }
            );
            this.progress = {
                currentIndex: 0,
                totalItems: 0
            };
            this.closeDialog();
        }
    }

    checkIfBriefingDirty() {
        if (this.isClean && this.briefingHistory &&
            (Number(this.briefingHistory?.submittedDate) >= Number(this.briefing.modifiedDate || this.briefing.createdDate))
        ) {
            this.setupIframe();
        } else {
            of(this.isClean).pipe(mergeMap(isClean => {
                if (!isClean) {
                    this.briefingsService.setCurrentBriefing(this.briefing);
                    return this.briefingsService.saveCurrentBriefing().pipe(map(x => {
                        this.isClean = true;
                        return true;
                    }));
                } else {
                    return of(true);
                }
            }),
            mergeMap(confirm => {
                if (confirm) {
                    return this.briefingsService.publishDraft(this.briefing.id, false);
                }
            }),
            catchError(x => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Briefing.pdf.generate.failed',
                    detail: this.localizationService.transform('Briefing.pdf.generate.error'),
                    sticky: true
                });
                this.closeDialog();
                throw x;
            }),
            take(1)).subscribe(x => {
                this.setupIframe();
            });
        }
    }

    confirm() {
        this.confirmGenerate = true;
        if (this.isReportBriefing) {
            this.startGeneratingPDF();
        } else {
            this.checkIfBriefingDirty();
        }
    }

    // BIG HACK for iFrame and PDF creating
    // so the PDF generation is in the BMIE repo, and what we are doing is putting an iframe on the app,
    // and calling the generate PDF in the BMIE repo (so the PDFs match).. and for some reason chrome does do any "loading" of the iframe
    // until there is some sore of interaction with it (to save resources), so as a hack, we create a mouse event that acts like we are putting the mouse
    // over the iframe, witch signals a "user interaction"
    doMouseIntervalOverIFrame() {
        this.clickTimeout = setInterval(() => {
            const evt = document.createEvent('MouseEvents');

            const max = 500, min = 1;
            evt.initMouseEvent('mousemove',
                true, false, window, null,
                10,10,
                Math.floor(Math.random() * (max - min + 1)) + min,
                Math.floor(Math.random() * (max - min + 1)) + min,
                null, null, null, null, null, null
            );

            // Dispatch the mousemove event on the iframe element
            this.iframeElement?.dispatchEvent(evt);
        }, 1000);
    }

    setupIframe() {
        this.reservedUrl = this.briefingsService.getReservedUrl();

        if (this.iframeElement && this.reservedUrl) {
            this.iframeElement.src = this.reservedUrl;
        }
    }

    startGeneratingPDF() {
        if (this.iframeElement?.contentWindow) {
            const message = {
                generatePDF: {
                    includeMentionsInPDF: this.includeMentionsInPDF
                }
            };
            this.iframeElement.contentWindow.postMessage(message, '*');
        }
        this.doMouseIntervalOverIFrame();
    }

    cancelPDF() {
        this.iframeElement?.contentWindow.postMessage('cancelPDF', '*');

        if (this.clickTimeout) {
            clearInterval(this.clickTimeout);
            this.clickTimeout = null;
        }
    }

    closeDialog() {
        this.cancelPDF();
        super.closeDialog();
    }
}

